import opentype from "opentype.js";
import { useRef } from "react";

export const getNestedValue = (obj: any, path: string) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

export const removeEmptyValues = (obj: object) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
};

export function useDebounceCallback(
  callback: (...args: any[]) => void,
  delay: number,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedFunction = (...args: any[]) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return debouncedFunction;
}

export const urlToFile = async (url: string, fileName?: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], fileName || url, { type: blob.type });
};

export const dataURLtoBlob = (dataUrl: string) => {
  const [header, base64] = dataUrl.split(",");
  const mimeType = header.match(/:(.*?);/)?.[1];

  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i += 512) {
    const slice = byteCharacters.slice(i, i + 512);
    const byteNumbers = new Array(slice.length)
      .fill(0)
      .map((_, j) => slice.charCodeAt(j));
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

export const splitPathAndName = (
  input: string,
): { path: string; name: string } => {
  const parts = input.split("/");
  const fileNameWithQuery = parts.pop() || "";
  const path = parts.join("/");
  const name = fileNameWithQuery.split("?")[0];
  return { path, name };
};

export const getFolderPath = (relativePath: string): string => {
  const lastSlashIndex = relativePath.lastIndexOf("/");
  if (lastSlashIndex === -1) return "";
  return relativePath.substring(0, lastSlashIndex);
};

const naturalSortFilesByName = (files: File[]): File[] => {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
};

export const naturalSortFileList = (files: File[], accept?: string) => {
  const folderFilesMap: Map<string, File[]> = new Map();

  files.forEach((file) => {
    if (accept && !file.type.startsWith(accept)) return;
    const folderPath = getFolderPath(file.webkitRelativePath);
    const folderFiles = folderFilesMap.get(folderPath) || [];
    folderFiles.push(file);
    folderFilesMap.set(folderPath, folderFiles);
  });

  const result: File[] = [];
  for (const [, files] of folderFilesMap) {
    const sortedFiles = naturalSortFilesByName(files);
    result.push(...sortedFiles);
  }

  return result;
};

export const getFileNameWithoutExt = (fileName: string) => {
  return fileName.includes(".")
    ? fileName.substring(0, fileName.lastIndexOf("."))
    : fileName;
};

const VALID_FONT_FILE_EXTENSIONS = new Set(["ttf", "otf", "woff", "woff2"]);

export const fileFilters: { [key: string]: (f: File) => Promise<boolean> } = {
  image: async (f: File) => f.type.startsWith("image"),
  font: async (f: File) => {
    try {
      const fileBuffer = await f.arrayBuffer();
      opentype.parse(fileBuffer);
    } catch (error) {
      console.error("Unsupported font file", error);
      return false;
    }

    if (f.type.startsWith("font")) return true;
    const [, ext] = f.name.split(".");
    return VALID_FONT_FILE_EXTENSIONS.has(ext.toLowerCase());
  },
};

export const fileUploadsPreprocess = async (files: File[], accept?: string) => {
  const checker = fileFilters[accept || ""];
  const validFiles = [];
  const invalidFiles = [];
  if (checker) {
    const results = await Promise.all(files.map((file) => checker(file)));
    for (let i = 0; i < files.length; i++) {
      if (results[i]) {
        validFiles.push(files[i]);
      } else {
        invalidFiles.push(files[i]);
      }
    }
  } else {
    validFiles.push(...files);
  }

  return { validFiles, invalidFiles };
};
